import { useEffect } from "react";
import { Link } from "wouter";
import type { LucideIcon } from "lucide-react";
import { ArrowRight } from "lucide-react";
import { HeaderBar } from "@/components/HeaderBar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useI18n } from "@/hooks/use-i18n";

export interface ProcedureItem {
  nameKey: string;
  descriptionKey: string;
  durationKey: string;
  recoveryKey: string;
}

export interface FaqItem {
  questionKey: string;
  answerKey: string;
}

export interface SpecialtyPageProps {
  icon: LucideIcon;
  titleKey: string;
  subtitleKey: string;
  overviewKey: string;
  procedures: ProcedureItem[];
  advantagesKeys: string[];
  faq: FaqItem[];
  priceFromKey: string;
  priceNoteKey: string;
  comparisonKey: string;
  ctaTitleKey: string;
}

export function SpecialtyPage({
  icon: Icon,
  titleKey,
  subtitleKey,
  overviewKey,
  procedures,
  advantagesKeys,
  faq,
  priceFromKey,
  priceNoteKey,
  comparisonKey,
  ctaTitleKey,
}: SpecialtyPageProps) {
  const { t, dir, lang, setLang } = useI18n();

  useEffect(() => {
    document.title = `${t(titleKey)} — Aurelian Private Care`;
  }, [t, titleKey, lang]);

  return (
    <div className="flex flex-col min-h-screen" dir={dir}>
      <HeaderBar lang={lang} onChangeLang={setLang} dir={dir} />
      <main className="flex-1">
        {/* Hero */}
        <section className="py-20 md:py-28 relative overflow-hidden">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <Icon className="w-10 h-10 text-primary" />
            </div>
            <h1
              className="text-3xl md:text-5xl font-serif font-bold text-foreground mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {t(titleKey)}
            </h1>
            <p className="text-lg text-muted-foreground mb-10">{t(subtitleKey)}</p>
            <Link href="/evaluation">
              <Button size="lg" className="min-h-12 px-8">
                {t("specialties.ctaEvaluation")}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </section>

        {/* Overview */}
        <section className="py-16 bg-card/20">
          <div className="max-w-3xl mx-auto px-6">
            <p className="text-muted-foreground leading-relaxed text-lg">{t(overviewKey)}</p>
          </div>
        </section>

        {/* Procedures */}
        <section className="py-20">
          <div className="max-w-6xl mx-auto px-6">
            <h2
              className="text-2xl font-serif font-bold text-foreground mb-10 text-center"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {t("specialties.proceduresTitle")}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {procedures.map((proc, i) => (
                <div
                  key={i}
                  className="bg-card/60 backdrop-blur-md border border-border/30 rounded-2xl p-6"
                >
                  <h3 className="font-serif font-semibold text-foreground mb-2">{t(proc.nameKey)}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{t(proc.descriptionKey)}</p>
                  <div className="flex gap-4 text-xs text-muted-foreground/80">
                    <span>{t("specialties.duration")}: {t(proc.durationKey)}</span>
                    <span>{t("specialties.recovery")}: {t(proc.recoveryKey)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Turkey */}
        <section className="py-20 bg-card/20">
          <div className="max-w-4xl mx-auto px-6">
            <h2
              className="text-2xl font-serif font-bold text-foreground mb-10 text-center"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {t("specialties.whyTurkeyTitle")}
            </h2>
            <ul className="space-y-4">
              {advantagesKeys.map((key, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 p-4 rounded-xl bg-background/60 border border-border/30"
                >
                  <span className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-primary text-sm font-medium shrink-0">
                    {i + 1}
                  </span>
                  {t(key)}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Price range */}
        <section className="py-20">
          <div className="max-w-xl mx-auto px-6">
            <div className="border-2 border-primary/30 rounded-2xl bg-card/60 backdrop-blur-md p-8 text-center">
              <p className="text-sm text-muted-foreground mb-2">{t("specialties.fromPrice")}</p>
              <p className="text-3xl font-serif font-bold text-primary mb-4">{t(priceFromKey)}</p>
              <p className="text-sm text-muted-foreground mb-4">{t(priceNoteKey)}</p>
              <p className="text-sm text-primary/90 font-medium">{t(comparisonKey)}</p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 bg-card/20">
          <div className="max-w-3xl mx-auto px-6">
            <h2
              className="text-2xl font-serif font-bold text-foreground mb-10 text-center"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {t("specialties.faqTitle")}
            </h2>
            <Accordion type="single" collapsible className="space-y-2">
              {faq.map((item, i) => (
                <AccordionItem
                  key={i}
                  value={`faq-${i}`}
                  className="border border-border/30 rounded-lg bg-background/60 px-5"
                >
                  <AccordionTrigger className="hover:no-underline py-5 text-left font-medium">
                    {t(item.questionKey)}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-5">
                    {t(item.answerKey)}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA final */}
        <section className="py-24 text-center">
          <div className="max-w-2xl mx-auto px-6">
            <h2
              className="text-2xl font-serif font-bold text-foreground mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {t(ctaTitleKey)}
            </h2>
            <Link href="/evaluation">
              <Button size="lg" className="min-h-12 px-8">
                {t("specialties.ctaEvaluation")}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <Footer t={t} dir={dir} />
    </div>
  );
}
